import React from 'react';
import { useOrganizers } from '../hooks/useData';
import OrganizerCard from './OrganizerCard';
import TeamCard from './TeamCard';

const OrganizersSection = () => {
  const { organizersData, loading, error } = useOrganizers();

  if (error) {
    return (
      <section id="organizers" className="section bg-light">
        <div className="container">
          <h2>🎪 Organizers</h2>
          <div className="error">
            <p>Error loading organizers: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  // Get organizers that are members of event organizer teams
  const getEventOrganizerMembers = () => {
    const memberIds = new Set();
    organizersData.eventOrganizers.forEach(eventOrg => {
      eventOrg.members?.forEach(memberId => memberIds.add(memberId));
    });

    const memberOrder = [];
    organizersData.eventOrganizers.forEach(eventOrg => {
      eventOrg.members?.forEach(memberId => memberOrder.push(memberId));
    });

    return organizersData.organizers
      .filter(org => memberIds.has(org.id))
      .sort((a, b) => memberOrder.indexOf(a.id) - memberOrder.indexOf(b.id));
  };

  const eventOrganizerMembers = getEventOrganizerMembers();

  return (
    <section id="organizers" className="section">
      <div className="container">
        <h2>🎪 Organizers</h2>
        <p>The team making this exceptional event possible</p>

        {loading ? (
          <div className="loading">Loading organizers...</div>
        ) : (
          <>
            {/* User Group Leaders Section */}
            <div className="organizers-subsection">
              <h3>👥 User Group Leaders</h3>
              <p>The AWS community leaders coordinating this event</p>
              <div className="organizers-grid">
                {/* Event organizers team card */}
                {organizersData.eventOrganizers.map((eventOrg) => (
                  <TeamCard
                    key={eventOrg.id}
                    team={eventOrg}
                  />
                ))}

                {/* Individual organizers - only those in eventOrganizers.members, sorted alphabetically */}
                {eventOrganizerMembers.map((organizer) => (
                  <OrganizerCard key={organizer.id} organizer={organizer} />
                ))}
              </div>
            </div>

            {/* Volunteers Section */}
            {organizersData.volunteers?.length > 0 && (
              <div className="organizers-subsection">
                <h3>🙌 Volunteers</h3>
                <p>Community members who stepped up to help make this event a success</p>
                <div className="organizers-grid">
                  {/* Volunteers team card */}
                  {organizersData.volunteersTeam && (
                    <TeamCard team={organizersData.volunteersTeam} />
                  )}

                  {/* Individual volunteers */}
                  {organizersData.volunteers.map((volunteerId) => {
                    const volunteer = organizersData.organizers.find(o => o.id === volunteerId);
                    return volunteer ? (
                      <OrganizerCard key={volunteer.id} organizer={volunteer} />
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* AWS Support Team Section */}
            <div className="organizers-subsection">
              <h3>☁️ AWS Team</h3>
              <p>The AWS team supporting and accompanying us for this event</p>

              {organizersData.awsHelpers?.map((awsHelper) => {
                // Get team members for this specific team
                const teamMembers = organizersData.awsStaff?.filter(staff =>
                  awsHelper.members?.includes(staff.id)
                ) || [];

                return (
                  <div key={awsHelper.id} className="aws-team-section">
                    <div className="organizers-grid">
                      {/* Team card */}
                      <TeamCard
                        team={awsHelper}
                      />

                      {/* Team members */}
                      {teamMembers.map((staff) => (
                        <OrganizerCard
                          key={staff.id}
                          organizer={staff}
                          isAwsHelper={true}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="contact-info">
          <h3>Contact</h3>
          <p>For any questions about the event, feel free to contact us.</p>
        </div>
      </div>
    </section>
  );
};

export default OrganizersSection;